import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IonicModule, ToastController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { PremiumChatService } from '../../../services/api/premium-chat.service';
import { StripeService } from '../../../services/stripe.service';
import { TranslateConfigService } from '../../../services/translate-config.service';
import { BrowserService } from '../../../services/browser.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { DialogComponent } from '../../dialog/dialog.component';

/**
 * Componente de bloqueo de chat premium.
 *
 * Se muestra cuando el chat premium no ha sido desbloqueado.
 * - Para el propietario del objeto: muestra importe + botón de pago.
 * - Para el finder: muestra mensaje informativo de espera.
 */
@Component({
  selector: 'app-premium-chat-lock',
  templateUrl: './premium-chat-lock.component.html',
  styleUrls: ['./premium-chat-lock.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, TranslateModule, FormsModule, DialogComponent]
})
export class PremiumChatLockComponent {
  @Input() conversationId!: number;
  @Input() isObjectOwner = false;
  @Input() premiumChatAmount = 0;
  @Input() currentUserId!: number;
  /** Contenido del mensaje gratuito del propietario, si ya fue enviado. */
  @Input() ownerFreeMessage: string = '';

  /** Se emite cuando el chat premium se desbloquea con éxito. */
  @Output() premiumChatUnlocked = new EventEmitter<void>();

  /** Se emite cuando el propietario salta el flujo de código y cierra el intercambio directamente. */
  @Output() directClose = new EventEmitter<void>();

  loading = false;
  showPaymentConfirm = false;
  termsAccepted = false;

  constructor(
    private premiumChatService: PremiumChatService,
    private stripeService: StripeService,
    private toastController: ToastController,
    private browserService: BrowserService,
    private router: Router,
    public t: TranslateConfigService,
  ) {}

  /**
   * Opens the payment confirmation dialog before proceeding to Stripe.
   */
  requestPay(): void {
    this.showPaymentConfirm = true;
  }

  cancelPaymentConfirm(): void {
    this.showPaymentConfirm = false;
    this.termsAccepted = false;
  }

  confirmPay(): void {
    this.showPaymentConfirm = false;
    this.termsAccepted = false;
    this.pay();
  }

  /** Opens Terms & Conditions in an external browser. */
  openTerms(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('terms-link')) {
      event.preventDefault();
      event.stopPropagation();
      this.browserService.openTermsAndConditions();
    }
  }

  openPublicProfileSettings(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.cancelPaymentConfirm();
    this.router.navigate(['/edit-public-profile']);
  }

  private async ensureChatStillLocked(): Promise<boolean> {
    const status = await firstValueFrom(this.premiumChatService.getAmount(this.conversationId));

    if (status.success && status.premium_chat_unlocked) {
      await this.showToast(
        this.t.getItemTranslated('PREMIUM_CHAT.ALREADY_UNLOCKED')
          || this.t.getItemTranslated('DETAILS.PAYMENT_ALREADY_PAID')
          || 'El chat premium ya está desbloqueado',
        'warning',
      );
      this.premiumChatUnlocked.emit();
      return false;
    }

    return true;
  }

  /**
   * Inicia el flujo de pago de Stripe para desbloquear el chat premium.
   */
  async pay(): Promise<void> {
    if (this.loading) return;
    if (!(await this.ensureChatStillLocked())) return;
    this.loading = true;

    try {
      const paymentResult = await this.stripeService.executeCustomFlow({
        prepareNative: async () => {
          const res = await firstValueFrom(
            this.premiumChatService.createIntent(this.conversationId),
          );
          if (!res.success) {
            throw new Error('Failed to create premium chat intent');
          }
          return {
            reference: res.tokenRef,
            options: {
              paymentIntentClientSecret: res.paymentIntent,
              customerId: res.customer,
              customerEphemeralKeySecret: res.ephemeralKey,
              merchantDisplayName: 'Foundek',
            },
          };
        },
        prepareWeb: async () => {
          const res = await firstValueFrom(
            this.premiumChatService.createCheckoutSession(this.conversationId),
          );
          if (!res.success) {
            throw new Error('Failed to create premium chat checkout session');
          }
          return {
            reference: res.session.id,
            clientSecret: res.session.client_secret,
          };
        },
        loadingKey: 'COMMON.LOADING',
        processingKey: 'PREMIUM_CHAT.PROCESSING',
        errorKey: 'PREMIUM_CHAT.ERROR',
      });

      const verifiedResult = await this.stripeService.verifyPaymentOutcome({
        initialResult: paymentResult,
        verify: async () => firstValueFrom(this.premiumChatService.getAmount(this.conversationId)),
        isVerified: (status) => status.success && status.premium_chat_unlocked === true,
      });

      if (verifiedResult.success) {
        await this.showToast(
          this.t.getItemTranslated('PREMIUM_CHAT.PAYMENT_SUCCESS') || 'Premium chat unlocked!',
          'success',
        );
        this.premiumChatUnlocked.emit();
        return;
      }

      if (!verifiedResult.canceled) {
        throw verifiedResult.error ?? new Error('PREMIUM_CHAT.ERROR');
      }
    } catch (err: any) {
      if (err?.message !== 'Payment canceled' && err?.message !== 'WEB_CHECKOUT_CLOSED') {
        await this.showToast(
          this.t.getItemTranslated('PREMIUM_CHAT.ERROR') || 'An error occurred',
          'danger',
        );
      }
    } finally {
      this.loading = false;
    }
  }

  private async showToast(message: string, color: string): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      color,
      position: 'top',
    });
    await toast.present();
  }
}
